import { Box, Button, Chip, CircularProgress } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ViewButton } from '../../Buttons/ViewButton';
import { EditButton } from '../../Buttons/EditButton';
import { IAdminUser } from '../../../types/store';

interface AdminUsersColumnCallbacks {
  onClickView: (user: IAdminUser) => void;
  onClickEdit: (user: IAdminUser) => void;
  onClickToggleEnabled: (user: IAdminUser) => void;
  onClickResetPassword: (user: IAdminUser) => void;
  onClickSetTemporaryPassword: (user: IAdminUser) => void;
  togglingEnabledId: number | null;
  resettingPasswordId: number | null;
  settingTempPasswordId: number | null;
}

export const getAdminUsersColumns = ({
  onClickView,
  onClickEdit,
  onClickToggleEnabled,
  onClickResetPassword,
  onClickSetTemporaryPassword,
  togglingEnabledId,
  resettingPasswordId,
  settingTempPasswordId,
}: AdminUsersColumnCallbacks): GridColDef[] => {
  return [
    {
      field: 'roles',
      headerName: 'Roles',
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const user: IAdminUser = params.row;
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
            {user.roles.map((role) => (
              <Chip key={role.id} label={role.name} size="small" />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 200,
    },
    {
      field: 'username',
      headerName: 'Username',
      minWidth: 140,
    },
    {
      field: 'first_name',
      headerName: 'Name',
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        return <>{`${params.row.first_name} ${params.row.last_name}`}</>;
      },
    },
    {
      field: 'phone_number',
      headerName: 'Phone Number',
      minWidth: 140,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const user: IAdminUser = params.row;
        return (
          <>
            <ViewButton onClick={() => onClickView(user)} />
            <EditButton onClick={() => onClickEdit(user)} />
          </>
        );
      },
    },
    {
      field: 'deactivate',
      headerName: 'Status',
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        const user: IAdminUser = params.row;
        if (togglingEnabledId === user.id) return <CircularProgress size={20} />;
        return (
          <Button
            style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
            variant="contained"
            color={user.enabled ? 'warning' : 'success'}
            onClick={() => onClickToggleEnabled(user)}
          >
            {user.enabled ? 'Deactivate' : 'Activate'}
          </Button>
        );
      },
    },
    {
      field: 'resetPassword',
      headerName: 'Reset Password',
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const user: IAdminUser = params.row;
        if (resettingPasswordId === user.id) return <CircularProgress size={20} />;
        return (
          <Button
            style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
            variant="contained"
            disabled={!user.enabled}
            onClick={() => onClickResetPassword(user)}
          >
            Reset Password
          </Button>
        );
      },
    },
    {
      field: 'setTemporaryPassword',
      headerName: 'Set Temp Password',
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const user: IAdminUser = params.row;
        if (settingTempPasswordId === user.id) return <CircularProgress size={20} />;
        return (
          <Button
            style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
            variant="contained"
            color="secondary"
            onClick={() => onClickSetTemporaryPassword(user)}
          >
            Set Temp Password
          </Button>
        );
      },
    },
  ];
};
